import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { useNavigate } from 'react-router-dom';
import { useNavigationStore } from '@/common/store';
import { Button } from '@/common/components/button/Button';
import { isValidEmail } from '@/common/utils/textFormat';
import { commonService } from '@/services/commonService';
import { toastUtils } from '@/common/utils/toastUtils';
import { CompanyData, companyService } from '@/services/companyService';
import {
  Search,
  Mail,
  Building2,
  Users,
  Check,
  ChevronRight,
  ArrowLeft,
  Sparkles,
  Info,
  AlertCircle,
  CheckCircle2,
  Send,
  Hash,
  User,
  FileText
} from 'lucide-react';
import { companyUserService } from '@/services/companyUserService';
import { useAuthStore } from '@/store/useAuthStore';

/**
 * 회사 찾기 페이지 컴포넌트
 * 1. 회사 검색
 * 2. 회사 선택
 * 3. 이메일 작성 및 중복검사
 * 4. 직원 참여 신청
 */

// 페이지 스텝 타입
type FindCompanyStep = 'SEARCH' | 'EMAIL_INPUT';

const FindCompanyPage: React.FC = () => {
  const navigate = useNavigate();
  const {   setHeaderTitle, setBackNavigation, clearBackNavigation } = useNavigationStore();
  const { user } = useAuthStore();
  
  // 사용자가 이미 속해있는 회사 ID 목록
  const userCompanyIds = user?.companyUsers?.map(cu => cu.company.id) || [];
  
  /**
   * 회사가 이미 가입되어 있는지 확인
   */
  const isAlreadyJoined = (companyId: number): boolean => {
    return userCompanyIds.includes(companyId);
  };

  // 상태 관리
  const [currentStep, setCurrentStep] = useState<FindCompanyStep>('SEARCH');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CompanyData[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);
  const [companyEmail, setCompanyEmail] = useState('');
  const [isEmailChecked, setIsEmailChecked] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // 뒤로가기 핸들러
  const handleGoBack = () => {
    if (currentStep === 'SEARCH') {
      navigate('/my-space');
    } else if (currentStep === 'EMAIL_INPUT') {
      setCurrentStep('SEARCH');
      setSelectedCompany(null);
    }
  };

  // 회사 검색 핸들러
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const results = await companyService.getSearchCompany(searchQuery);
      setSearchResults(results);
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  // 회사 선택 핸들러
  const handleCompanySelect = (company: CompanyData) => {
    setSelectedCompany(company);
    setCurrentStep('EMAIL_INPUT');
  };

  // 이메일 중복검사 핸들러
  const handleEmailCheck = async () => {
    if (!companyEmail.trim()) {
      setEmailError('이메일을 입력해주세요.');
      return;
    }

    if (!isValidEmail(companyEmail)) {
      setEmailError('올바른 이메일 형식을 입력해주세요.');
      return;
    }

    setIsCheckingEmail(true);
    setEmailError('');

    try {
      const result = await companyUserService.checkDuplicateEmail(companyEmail);

      if (result.exists) {
        setEmailError('이미 사용 중인 이메일입니다.');
        setIsEmailChecked(false);
      } else {
        setIsEmailChecked(true);
        setEmailError('');
        toastUtils.success('사용 가능한 이메일입니다.');
      }
    } catch (error) {
      toastUtils.error('이메일 확인 중 오류가 발생했습니다.');
      setIsEmailChecked(false);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  // 참여 신청 핸들러
  const handleJoinRequest = async () => {
    if (!selectedCompany || !isEmailChecked) return;

    setIsSubmitting(true);

    try {
      const joinRequestData = {
        companyId: selectedCompany.id,
        email: companyEmail
      };

      const result = await companyService.applyCompanyJoinRequest(joinRequestData);
      if (result.success) {
        toastUtils.success('참여 신청이 완료되었습니다. 마스터 승인을 기다려주세요.');
        navigate('/my-space');
      } else {
        toastUtils.error(result.message || '참여 신청 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('참여 신청 실패:', error);
      setCurrentStep('EMAIL_INPUT');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    
    setHeaderTitle('회사 찾기');
    clearBackNavigation();
    setBackNavigation('마이스페이스', '/my-space');
  }, [  setHeaderTitle, clearBackNavigation, setBackNavigation]);

  return (
    <PageContainer>
      <ContentWrapper>
        {/* 히어로 섹션 */}
        <HeroSection>
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroIconWrapper>
              <Search size={32} />
            </HeroIconWrapper>
            <HeroTextContent>
              <HeroTitle>회사 찾기</HeroTitle>
              <HeroDescription>
                참여하고 싶은 회사를 검색하고, 조직원으로 가입 신청하세요.
                <br />
                마스터 승인 후 회사의 스페이스를 이용할 수 있습니다.
              </HeroDescription>
            </HeroTextContent>
          </HeroContent>
        </HeroSection>

        {/* 스텝 인디케이터 */}
        <StepIndicator>
          <StepItem $active={currentStep === 'SEARCH'} $completed={currentStep === 'EMAIL_INPUT'}>
            <StepNumber $active={currentStep === 'SEARCH'} $completed={currentStep === 'EMAIL_INPUT'}>
              {currentStep === 'EMAIL_INPUT' ? <Check size={16} /> : '1'}
            </StepNumber>
            <StepInfo>
              <StepLabel $active={currentStep === 'SEARCH'}>회사 검색</StepLabel>
              <StepSublabel>참여할 회사 선택</StepSublabel>
            </StepInfo>
          </StepItem>

          <StepConnector $completed={currentStep === 'EMAIL_INPUT'} />

          <StepItem $active={currentStep === 'EMAIL_INPUT'} $completed={false}>
            <StepNumber $active={currentStep === 'EMAIL_INPUT'} $completed={false}>
              2
            </StepNumber>
            <StepInfo>
              <StepLabel $active={currentStep === 'EMAIL_INPUT'}>가입 신청</StepLabel>
              <StepSublabel>이메일 입력 및 신청</StepSublabel>
            </StepInfo>
          </StepItem>
        </StepIndicator>

        {/* 메인 콘텐츠 */}
        <MainContent>
          {currentStep === 'SEARCH' && (
            <SearchStep>
              {/* 검색 카드 */}
              <SearchCard>
                <SearchCardHeader>
                  <SearchCardIcon>
                    <Search size={20} />
                  </SearchCardIcon>
                  <SearchCardTitle>회사 검색</SearchCardTitle>
                </SearchCardHeader>

                <SearchInputWrapper>
                  <SearchIconInner>
                    <Search size={20} />
                  </SearchIconInner>
                  <SearchInput
                    type="text"
                    placeholder="회사명, 사업자번호( - 포함), 회사코드로 검색"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <SearchButton onClick={handleSearch} disabled={!searchQuery.trim() || isSearching}>
                    {isSearching ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <Search size={18} />
                        <span>검색</span>
                      </>
                    )}
                  </SearchButton>
                </SearchInputWrapper>

                <SearchTips>
                  <TipIcon>
                    <Info size={16} />
                  </TipIcon>
                  <TipContent>
                    <TipText>정확한 회사명, 사업자번호(하이픈 포함) 또는 회사코드로 검색하세요.</TipText>
                  </TipContent>
                </SearchTips>
              </SearchCard>

              {/* 검색 결과 */}
              {hasSearched && (
                <ResultsSection>
                  {searchResults.length > 0 ? (
                    <>
                      <ResultsHeader>
                        <ResultsTitle>
                          <Building2 size={20} />
                          <span>검색 결과</span>
                        </ResultsTitle>
                        <ResultsCount>{searchResults.length}개의 회사</ResultsCount>
                      </ResultsHeader>

                      <CompanyGrid>
                        {searchResults.map((company, index) => {
                          const alreadyJoined = isAlreadyJoined(company.id);
                          return (
                            <CompanyCard
                              key={company.id}
                              $delay={index * 0.1}
                              $disabled={alreadyJoined}
                              onClick={() => !alreadyJoined && handleCompanySelect(company)}
                            >
                              <CompanyCardTop>
                                <CompanyAvatarWrapper $disabled={alreadyJoined}>
                                  <Building2 size={24} />
                                </CompanyAvatarWrapper>
                                <CompanyMainInfo>
                                  <CompanyName $disabled={alreadyJoined}>{company.name}</CompanyName>
                                  <CompanyCode>
                                    <Hash size={12} />
                                    {company.code}
                                  </CompanyCode>
                                </CompanyMainInfo>
                                {alreadyJoined ? (
                                  <AlreadyJoinedBadge>
                                    <Check size={14} />
                                    이미 가입됨
                                  </AlreadyJoinedBadge>
                                ) : (
                                  <SelectBadge>
                                    선택
                                    <ChevronRight size={16} />
                                  </SelectBadge>
                                )}
                              </CompanyCardTop>

                              <CompanyDetails>
                                <DetailItem>
                                  <DetailIcon>
                                    <FileText size={14} />
                                  </DetailIcon>
                                  <DetailLabel>사업자번호</DetailLabel>
                                  <DetailValue>{company.businessNumber}</DetailValue>
                                </DetailItem>
                                <DetailItem>
                                  <DetailIcon>
                                    <User size={14} />
                                  </DetailIcon>
                                  <DetailLabel>대표자</DetailLabel>
                                  <DetailValue>{company.ceoName.join(', ')}</DetailValue>
                                </DetailItem>
                              </CompanyDetails>

                              {company.description && (
                                <CompanyDescription>{company.description}</CompanyDescription>
                              )}
                            </CompanyCard>
                          );
                        })}
                      </CompanyGrid>
                    </>
                  ) : (
                    <EmptyResults>
                      <EmptyIcon>
                        <Search size={48} />
                      </EmptyIcon>
                      <EmptyTitle>검색 결과가 없습니다</EmptyTitle>
                      <EmptyDescription>
                        '<EmptyKeyword>{searchQuery}</EmptyKeyword>'에 대한 결과를 찾을 수 없습니다.
                      </EmptyDescription>
                      <EmptyTips>
                        <EmptyTipTitle>
                          <Sparkles size={16} />
                          검색 팁
                        </EmptyTipTitle>
                        <EmptyTipList>
                          <EmptyTipItem>정확한 회사명을 입력해보세요</EmptyTipItem>
                          <EmptyTipItem>사업자번호는 하이픈(-)을 포함해서 입력해보세요</EmptyTipItem>
                          <EmptyTipItem>회사코드를 정확히 입력해보세요</EmptyTipItem>
                        </EmptyTipList>
                      </EmptyTips>
                    </EmptyResults>
                  )}
                </ResultsSection>
              )}
            </SearchStep>
          )}

          {currentStep === 'EMAIL_INPUT' && selectedCompany && (
            <EmailStep>
              {/* 선택된 회사 정보 */}
              <SelectedCompanyCard>
                <SelectedCompanyHeader>
                  <SelectedCompanyIcon>
                    <Check size={20} />
                  </SelectedCompanyIcon>
                  <SelectedCompanyLabel>선택한 회사</SelectedCompanyLabel>
                </SelectedCompanyHeader>

                <SelectedCompanyInfo>
                  <SelectedCompanyAvatar>
                    <Building2 size={28} />
                  </SelectedCompanyAvatar>
                  <SelectedCompanyDetails>
                    <SelectedCompanyName>{selectedCompany.name}</SelectedCompanyName>
                    <SelectedCompanyMeta>
                      <MetaBadge>
                        <Hash size={12} />
                        {selectedCompany.code}
                      </MetaBadge>
                      <MetaDivider>•</MetaDivider>
                      <MetaText>{selectedCompany.ceoName.join(', ')}</MetaText>
                    </SelectedCompanyMeta>
                  </SelectedCompanyDetails>
                  <ChangeButton onClick={handleGoBack}>
                    변경
                  </ChangeButton>
                </SelectedCompanyInfo>
              </SelectedCompanyCard>

              {/* 이메일 입력 카드 */}
              <EmailInputCard>
                <EmailCardHeader>
                  <EmailCardIcon>
                    <Mail size={20} />
                  </EmailCardIcon>
                  <EmailCardTitleGroup>
                    <EmailCardTitle>회사 이메일 입력</EmailCardTitle>
                    <EmailCardSubtitle>회사에서 사용할 이메일 주소를 입력해주세요</EmailCardSubtitle>
                  </EmailCardTitleGroup>
                </EmailCardHeader>

                <EmailInputWrapper>
                  <EmailInputGroup>
                    <EmailIconInner>
                      <Mail size={18} />
                    </EmailIconInner>
                    <EmailInput
                      type="email"
                      placeholder="company-email@example.com"
                      value={companyEmail}
                      onChange={(e) => {
                        setCompanyEmail(e.target.value);
                        setIsEmailChecked(false);
                        setEmailError('');
                      }}
                    />
                    <CheckButton
                      onClick={handleEmailCheck}
                      disabled={!companyEmail.trim() || isCheckingEmail}
                      $checked={isEmailChecked}
                    >
                      {isCheckingEmail ? (
                        <LoadingSpinner />
                      ) : isEmailChecked ? (
                        <>
                          <CheckCircle2 size={16} />
                          확인완료
                        </>
                      ) : (
                        '중복확인'
                      )}
                    </CheckButton>
                  </EmailInputGroup>

                  {emailError && (
                    <EmailMessage $type="error">
                      <AlertCircle size={14} />
                      {emailError}
                    </EmailMessage>
                  )}
                  {isEmailChecked && (
                    <EmailMessage $type="success">
                      <CheckCircle2 size={14} />
                      사용 가능한 이메일입니다
                    </EmailMessage>
                  )}
                </EmailInputWrapper>

                <EmailNotice>
                  <NoticeIcon>
                    <Info size={16} />
                  </NoticeIcon>
                  <NoticeContent>
                    <NoticeTitle>안내사항</NoticeTitle>
                    <NoticeText>
                      입력하신 이메일로 회사 관련 알림이 발송됩니다.
                      <br />
                      가입 신청 후 회사 마스터의 승인이 필요합니다.
                    </NoticeText>
                  </NoticeContent>
                </EmailNotice>
              </EmailInputCard>

              {/* 액션 버튼 */}
              <ActionButtons>
                <BackButton onClick={handleGoBack}>
                  <ArrowLeft size={18} />
                  이전 단계
                </BackButton>
                <SubmitButton
                  onClick={handleJoinRequest}
                  disabled={!isEmailChecked || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner />
                      신청 중...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      가입 신청하기
                    </>
                  )}
                </SubmitButton>
              </ActionButtons>
            </EmailStep>
          )}
        </MainContent>
      </ContentWrapper>
    </PageContainer>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  min-height: 100%;
  height: 100%;
  padding: 1.5rem;
  background: ${semanticColors.background.primary};
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
`;

// 히어로 섹션
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 24px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 50%, #a78bfa 100%);
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  
  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border-radius: 16px;
  color: #fff;
  flex-shrink: 0;
`;

const HeroTextContent = styled.div`
  flex: 1;
`;

const HeroTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const HeroDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.85);
  line-height: 1.6;
`;

// 스텝 인디케이터
const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  margin-bottom: 2rem;
  animation: ${fadeInUp} 0.6s ease-out 0.1s both;
`;

const StepItem = styled.div<{ $active: boolean; $completed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  opacity: ${props => (props.$active || props.$completed) ? 1 : 0.4};
  transition: opacity 0.3s ease;
`;

const StepNumber = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  
  ${props => props.$completed && `
    background: ${semanticColors.background.success.primary};
    color: #fff;
  `}
  
  ${props => props.$active && !props.$completed && `
    background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
    color: #fff;
  `}
  
  ${props => !props.$active && !props.$completed && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.tertiary};
  `}
`;

const StepInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const StepLabel = styled.span<{ $active: boolean }>`
  ${typography.subtitle.subtitle2}
  color: ${props => props.$active ? semanticColors.text.primary : semanticColors.text.tertiary};
`;

const StepSublabel = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const StepConnector = styled.div<{ $completed: boolean }>`
  width: 60px;
  height: 2px;
  background: ${props => props.$completed
    ? semanticColors.background.success.primary
    : semanticColors.border.primary};
  transition: background 0.3s ease;
  
  @media (max-width: 640px) {
    width: 30px;
  }
`;

// 메인 콘텐츠
const MainContent = styled.div`
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

// 검색 스텝
const SearchStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SearchCard = styled.div`
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
`;

const SearchCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const SearchCardIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  border-radius: 10px;
  color: #fff;
`;

const SearchCardTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: ${semanticColors.background.primary};
  border: 2px solid ${semanticColors.border.primary};
  border-radius: 14px;
  transition: border-color 0.2s ease;
  
  &:focus-within {
    border-color: #8b5cf6;
  }
`;

const SearchIconInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 0.75rem;
  color: ${semanticColors.text.tertiary};
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.875rem 0.5rem;
  border: none;
  background: transparent;
  font-size: 15px;
  color: ${semanticColors.text.primary};
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }
`;

const SearchButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SearchTips = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 10px;
`;

const TipIcon = styled.div`
  color: ${semanticColors.text.brand};
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const TipContent = styled.div`
  flex: 1;
`;

const TipText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

// 검색 결과
const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ResultsTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
`;

const ResultsCount = styled.span`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.brand};
  padding: 0.375rem 0.875rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 100px;
`;

const CompanyGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CompanyCard = styled.div<{ $delay: number; $disabled?: boolean }>`
  padding: 1.5rem;
  background: ${props => props.$disabled 
    ? semanticColors.background.tertiary 
    : semanticColors.background.secondary};
  border: 2px solid ${props => props.$disabled 
    ? semanticColors.border.tertiary 
    : semanticColors.border.primary};
  border-radius: 16px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  opacity: ${props => props.$disabled ? 0.7 : 1};
  
  &:hover {
    ${props => !props.$disabled && `
      border-color: #8b5cf6;
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    `}
  }
`;

const CompanyCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

const CompanyAvatarWrapper = styled.div<{ $disabled?: boolean }>`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$disabled 
    ? semanticColors.background.tertiary 
    : `linear-gradient(135deg, ${semanticColors.background.tertiary} 0%, ${semanticColors.background.tertiaryHover} 100%)`};
  border-radius: 14px;
  color: ${props => props.$disabled ? semanticColors.text.tertiary : semanticColors.text.brand};
  flex-shrink: 0;
`;

const CompanyMainInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyName = styled.h3<{ $disabled?: boolean }>`
  ${typography.subtitle.subtitle1}
  color: ${props => props.$disabled ? semanticColors.text.tertiary : semanticColors.text.primary};
  margin-bottom: 0.375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanyCode = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
  font-variant-numeric: tabular-nums;
  padding: 0.25rem 0.5rem;
  background: ${semanticColors.background.primary};
  border-radius: 6px;
`;

const SelectBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  border-radius: 10px;
  ${typography.caption.caption1.medium}
  color: #fff;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  
  ${CompanyCard}:hover & {
    transform: translateX(4px);
  }
`;

const AlreadyJoinedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: ${semanticColors.background.success.secondary};
  border: 1px solid ${semanticColors.border.success};
  border-radius: 10px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.onSuccess.primary};
  flex-shrink: 0;
`;

const CompanyDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  background: ${semanticColors.background.primary};
  border-radius: 10px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DetailIcon = styled.div`
  color: ${semanticColors.text.tertiary};
`;

const DetailLabel = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  min-width: 70px;
`;

const DetailValue = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.primary};
`;

const CompanyDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${semanticColors.border.primary};
  line-height: 1.5;
`;

// 빈 결과
const EmptyResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  background: ${semanticColors.background.secondary};
  border: 2px dashed ${semanticColors.border.primary};
  border-radius: 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.primary};
  border-radius: 20px;
  color: ${semanticColors.text.disabled};
  margin-bottom: 1.5rem;
`;

const EmptyTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const EmptyDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 1.5rem;
`;

const EmptyKeyword = styled.span`
  color: #8b5cf6;
  font-weight: 600;
`;

const EmptyTips = styled.div`
  padding: 1.25rem;
  background: ${semanticColors.background.primary};
  border-radius: 12px;
  width: 100%;
  max-width: 350px;
`;

const EmptyTipTitle = styled.h4`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.75rem;
`;

const EmptyTipList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const EmptyTipItem = styled.li`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  padding-left: 1rem;
  position: relative;
  text-align: left;
  
  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #8b5cf6;
  }
`;

// 이메일 스텝
const EmailStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SelectedCompanyCard = styled.div`
  padding: 1.5rem;
  background: ${semanticColors.background.success.secondary};
  border: 1px solid ${semanticColors.border.success};
  border-radius: 16px;
`;

const SelectedCompanyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SelectedCompanyIcon = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.success.primary};
  border-radius: 50%;
  color: #fff;
`;

const SelectedCompanyLabel = styled.span`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.onSuccess.primary};
`;

const SelectedCompanyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SelectedCompanyAvatar = styled.div`
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.secondary};
  border-radius: 14px;
  color: ${semanticColors.text.brand};
  flex-shrink: 0;
`;

const SelectedCompanyDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const SelectedCompanyName = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.375rem;
`;

const SelectedCompanyMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
  font-variant-numeric: tabular-nums;
`;

const MetaDivider = styled.span`
  color: ${semanticColors.text.disabled};
`;

const MetaText = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.secondary};
`;

const ChangeButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: ${semanticColors.background.primaryHover};
    border-color: ${semanticColors.border.secondary};
  }
`;

// 이메일 입력 카드
const EmailInputCard = styled.div`
  padding: 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
`;

const EmailCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const EmailCardIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  border-radius: 12px;
  color: #fff;
  flex-shrink: 0;
`;

const EmailCardTitleGroup = styled.div`
  flex: 1;
`;

const EmailCardTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const EmailCardSubtitle = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const EmailInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const EmailInputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: ${semanticColors.background.primary};
  border: 2px solid ${semanticColors.border.primary};
  border-radius: 14px;
  transition: border-color 0.2s ease;
  
  &:focus-within {
    border-color: #8b5cf6;
  }
`;

const EmailIconInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 0.75rem;
  color: ${semanticColors.text.tertiary};
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 0.875rem 0.5rem;
  border: none;
  background: transparent;
  font-size: 15px;
  color: ${semanticColors.text.primary};
  
  &:focus {
    outline: none;
  }
  
  &::placeholder {
    color: ${semanticColors.text.tertiary};
  }
`;

const CheckButton = styled.button<{ $checked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  background: ${props => props.$checked
    ? semanticColors.background.success.primary
    : semanticColors.background.tertiary};
  border: none;
  border-radius: 10px;
  ${typography.caption.caption1.medium}
  color: ${props => props.$checked ? '#fff' : semanticColors.text.brand};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    background: ${props => props.$checked
      ? semanticColors.background.success.primaryHover
      : semanticColors.background.tertiaryHover};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmailMessage = styled.div<{ $type: 'error' | 'success' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  ${typography.caption.caption1.default}
  
  ${props => props.$type === 'error' && `
    background: ${semanticColors.background.danger.secondary};
    color: ${semanticColors.text.onDanger.secondary};
  `}
  
  ${props => props.$type === 'success' && `
    background: ${semanticColors.background.success.secondary};
    color: ${semanticColors.text.onSuccess.primary};
  `}
`;

const EmailNotice = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
  padding: 1rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 12px;
`;

const NoticeIcon = styled.div`
  color: ${semanticColors.text.brand};
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const NoticeContent = styled.div`
  flex: 1;
`;

const NoticeTitle = styled.p`
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.25rem;
`;

const NoticeText = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  line-height: 1.5;
`;

// 액션 버튼
const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 1.5rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.primaryHover};
    border-color: ${semanticColors.border.secondary};
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
  border: none;
  border-radius: 12px;
  ${typography.paragraph.p2.default}
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(124, 58, 237, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export default FindCompanyPage;
